// Sample invitations for the marketing pages and the template gallery, so
// every design can be shown fully furnished — story, programme, gallery —
// without shipping stock photography. Pictures are small illustrations
// generated from each template's own palette.
import { sampleEvent } from "./samples";
import { stockVideo } from "./templates";

const solid = (colour) => colour.match(/#[0-9a-f]{3,8}/i)?.[0] || "#888888";

const rand = (seed) => {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return value - Math.floor(value);
};

// "#rrggbb" blended towards another colour by `amount` (0–1).
const mix = (from, to, amount) => {
  const parse = (hex) => {
    const full = hex.length === 4 ? hex.replace(/[0-9a-f]/gi, "$&$&") : hex;
    return [1, 3, 5].map((start) => parseInt(full.slice(start, start + 2), 16));
  };
  const [a, b] = [parse(from), parse(to)];
  return `#${a.map((channel, index) => Math.round(channel + (b[index] - channel) * amount).toString(16).padStart(2, "0")).join("")}`;
};

// A small illustrated landscape — sky, sun and three ranges of hills — drawn
// in the template's colours. It is deliberately a crisp drawing, so it reads
// as a sample picture standing in for the host's photo. `seed` varies the
// composition, giving each demo a set of different pictures.
export const art = (template, seed = 1) => {
  const { bg, accent, accent2, ink } = template.palette;
  const paper = solid(bg);
  // Translucent accents (gradient templates) fall back to the ink colour.
  const main = accent.startsWith("#") ? accent : solid(ink);
  const second = accent2.startsWith("#") ? accent2 : main;
  const dark = Boolean(template.dark);

  const sky = dark ? [mix(paper, "#000000", 0.25), mix(paper, main, 0.35)] : [mix(second, "#ffffff", 0.6), mix(second, "#ffffff", 0.15)];
  const sun = dark ? mix(main, "#ffffff", 0.45) : "#ffffff";
  const hills = dark
    ? [mix(paper, main, 0.3), mix(paper, "#000000", 0.25), mix(paper, "#000000", 0.55)]
    : [mix(main, "#ffffff", 0.5), mix(main, "#ffffff", 0.18), mix(main, solid(ink), 0.5)];

  const sunX = 90 + rand(seed) * 220;
  const sunY = 110 + rand(seed + 1) * 110;
  const ridge = (level, index) => {
    const lift = (offset) => (level + (rand(seed * 7 + index * 3 + offset) - 0.5) * 90).toFixed(0);
    return `M0 ${lift(1)}C80 ${lift(2)} 150 ${lift(3)} 210 ${lift(4)}S340 ${lift(5)} 400 ${lift(6)}V500H0Z`;
  };
  const birds = [0, 1, 2]
    .map((index) => {
      const x = 60 + rand(seed + index * 5) * 260;
      const y = 60 + rand(seed + index * 9) * 90;
      return `<path d='M${x.toFixed(0)} ${y.toFixed(0)}q7 -7 14 0q7 -7 14 0' fill='none' stroke='${hills[2]}' stroke-width='2.5' stroke-linecap='round'/>`;
    })
    .join("");

  return `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 500' preserveAspectRatio='xMidYMid slice'>
      <defs><linearGradient id='s' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='${sky[0]}'/><stop offset='1' stop-color='${sky[1]}'/></linearGradient></defs>
      <rect width='400' height='500' fill='url(#s)'/>
      <circle cx='${sunX.toFixed(0)}' cy='${sunY.toFixed(0)}' r='46' fill='${sun}'/>
      ${birds}
      <path d='${ridge(300, 0)}' fill='${hills[0]}'/>
      <path d='${ridge(360, 1)}' fill='${hills[1]}'/>
      <path d='${ridge(425, 2)}' fill='${hills[2]}'/>
    </svg>`
  )}`;
};

// The sample couple, Amina and David, shown wherever a wedding needs a cover
// picture. It is an illustration; to use a photograph or generated image
// instead, add the file to public/images and change the file name here.
export const samplePhoto = `${process.env.PUBLIC_URL || ""}/images/couple.svg`;

const SCHEDULES = {
  wedding: ["ceremony", "cocktail", "dinner"],
  meeting: ["registration", "keynote", "networking"],
  default: ["welcome", "main", "party"],
};

// Templates whose demo opens with the bundled video instead of a picture.
// Wedding demos always show the sample couple.
const VIDEO_DEMOS = ["terracotta", "minuit-dore"];

export const demoEvent = (t, type, template, { bare = false } = {}) => {
  // Children's themes get a child's party as their sample content.
  const sample = sampleEvent(t, type);
  const base = template.kids
    ? { ...sample, title: t(`samples.kids.${template.kids}.title`), hosts: t(`samples.kids.${template.kids}.hosts`) }
    : sample;
  const voice = template.kids ? "kids" : type;
  const withVideo = VIDEO_DEMOS.includes(template.id) && type !== "wedding";
  const cover = type === "wedding" ? samplePhoto : art(template, 1);
  if (bare) return { ...base, look: { colorway: "default", gender: "neutral" }, showCountdown: false };
  const headings = t(`editor.story.presets.${type}`, { returnObjects: true });
  const schedule = SCHEDULES[type] || SCHEDULES.default;
  return {
    ...base,
    message: t(`demo.message.${voice}`),
    dressCode: type === "wedding" ? t("demo.dress") : "",
    // Video demos use the clip's own still frame as their cover picture.
    coverUrl: withVideo ? stockVideo("bloom").poster : cover,
    video: withVideo ? { stockId: "bloom" } : undefined,
    galleryUrls: [2, 3, 4, 5, 6].map((seed) => art(template, seed)),
    showCountdown: true,
    look: { colorway: "default", gender: "neutral" },
    story: (Array.isArray(headings) ? headings : []).slice(0, 2).map((heading, index) => ({
      id: `demo-${index}`,
      heading,
      text: t(`demo.story.${voice}.${index}`),
      photoUrl: art(template, 7 + index),
    })),
    schedule: schedule.map((key, index) => ({
      id: `demo-s${index}`,
      time: ["16:00", "18:00", "19:30"][index],
      label: t(`demo.schedule.${key}`),
    })),
  };
};
